import { getBootstrapAttribution } from "./runtime";
import type { AttributionWindowLike } from "./runtime";

export interface AttributionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AttributionStorageTarget extends AttributionWindowLike {
  location: { href: string };
  sessionStorage?: AttributionStorageLike;
  localStorage?: AttributionStorageLike;
}

export const getAttributionTarget = (
  target?: AttributionStorageTarget
): AttributionStorageTarget | undefined => {
  return target ?? (typeof window === "undefined" ? undefined : window);
};

export const getAttributionScope = (
  target: AttributionStorageTarget
): string | undefined => {
  const { countryCode, domainPid } = getBootstrapAttribution(target);
  if (!countryCode?.trim() || !domainPid.trim()) {
    return undefined;
  }

  try {
    const { origin } = new URL(target.location.href);
    if (origin === "null") {
      return undefined;
    }

    return `${origin}:${countryCode.trim().toLowerCase()}:${encodeURIComponent(
      domainPid.trim()
    )}`;
  } catch (_error) {
    return undefined;
  }
};

export const getAttributionStorage = (
  target: AttributionStorageTarget,
  kind: "sessionStorage" | "localStorage"
): AttributionStorageLike | undefined => {
  try {
    return target[kind];
  } catch (_error) {
    return undefined;
  }
};

export const readAttributionRecord = (
  storage: AttributionStorageLike | undefined,
  key: string
): unknown => {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch (_error) {
    return undefined;
  }
};

export const writeAttributionRecord = (
  storage: AttributionStorageLike | undefined,
  key: string,
  value?: unknown
): void => {
  try {
    if (value === undefined) {
      storage?.removeItem(key);
    } else {
      storage?.setItem(key, JSON.stringify(value));
    }
  } catch (_error) {
    // Browser storage can be unavailable; attribution must not block the hall.
  }
};
